import React, {useEffect, useMemo, useRef} from 'react'
import PageLayout from '../../components/layout/PageLayout'
import Text from '../../components/text/Text'
import WizImage from '../../components/image/WizImage'
import {useTime} from '../../utils/time'
import WizButton from '../../components/button/WizButton'
import useQueryTraderMapCampaign from '../../hooks/api/tradermap_campaign/useQueryTraderMapCampaign'
import Span from '../../components/text/Span'
import {useDisplayNumber} from '../../utils/number'
import useQueryExchanges from '../../hooks/etc/useQueryExchanges'
import useQueryTraderMapCampaignDetail from '../../hooks/api/tradermap_campaign/useQueryTraderMapCampaignDetail copy'
import {postUploadImage} from '../../apis/image/postUploadImage'
import UnderLineInput from '../../components/input/UnderLineInput'
import DateInput from '../../components/input/DateInput'
import WizSelect from '../../components/select/WizSelect'
import {createTraderMapCampaign} from '../../apis/tradermap_campaign/create'
import {editTraderMapCampaign} from '../../apis/tradermap_campaign/edit'
import useQueryTraderMapProjects from '../../hooks/api/tradermap_project/useQueryTraderMapProjects'
import {IOptions} from '../../components/select/constSelect'
import {image} from '@uiw/react-md-editor'
import useQueryLeaderboardBanner from '../../hooks/api/banner/useQueryLeaderboardBanner'
import img_placeholder from '../../assets/images/placeholder.png'

interface IProps {
    className?: string
}

export function TraderMapCampaign({className}: IProps) {
    const [active, setActive] = React.useState<'default' | 'create' | 'detail'>('default')
    const {data: campaigns, refetch} = useQueryTraderMapCampaign()
    const {data: exchanges} = useQueryExchanges()
    const now = new Date()?.getTime()
    const {displayAmount} = useDisplayNumber()
    const {formatUTCDate, localTimeString, convertInputToTimezone, convertTimezoneToInput} = useTime()

    const [id, setId] = React.useState<string>('')
    const {data: detailData, refetch: detailRefetch} = useQueryTraderMapCampaignDetail(id)

    // project list

    const {data: projects} = useQueryTraderMapProjects()
    // const {data: banners} = useQueryLeaderboardBanner()

    const list = useMemo<IOptions[]>(() => {
        return projects?.map(project => ({
            id: project?.id,
            value: project?.symbol,
            image: project?.symbol_image,
        }))
    }, [projects])

    // reset

    const resetValue = useMemo(() => {
        return {
            project_id: list?.at(0)?.id,
            banner_id: {
                en: '',
                ko: '',
            },
            title: {
                en: '',
                ko: '',
            },
            description: {
                en: '',
                ko: '',
            },
            symbol: '',
            priority: undefined,
            symbol_image: '',
            exchanges: exchanges?.reduce((acc, key) => {
                acc[key?.name] = ''
                return acc
            }, {} as {[key: string]: string}),
            start_at: '',
            end_at: '',
            prize_pool: {
                total_amount: 0,
                currency: '',
            },
            url_third_party: '',
        }
    }, [list])

    const [body, setBody] = React.useState(resetValue)

    const symbolImageRef = useRef<any>()

    const onChangeThumbnail = async e => {
        const imageFile = e?.target?.files[0]

        if (imageFile) {
            const result = await postUploadImage(imageFile)
            setBody(prev => ({...prev, symbol_image: result?.image_url}))
        }
    }

    useEffect(() => {
        if (detailData) {
            setBody({
                project_id: detailData?.project_id || '',
                banner_id: detailData?.banner_id || {en: '', ko: ''},
                title: detailData?.title || {en: '', ko: ''},
                description: detailData?.description || {en: '', ko: ''},
                symbol: detailData?.symbol || '',
                priority: detailData?.priority,
                symbol_image: detailData?.symbol_image || '',
                exchanges: exchanges?.reduce((acc, key) => {
                    acc[key?.name] = detailData?.exchanges[key?.name] || ''
                    return acc
                }, {} as {[key: string]: string}),
                start_at: detailData?.start_at,
                end_at: detailData?.end_at,
                prize_pool: {
                    total_amount: detailData?.prize_pool?.total_amount || 0,
                    currency: detailData?.prize_pool?.currency || '',
                },
                url_third_party: detailData?.url_third_party || '',
            })
        }
    }, [detailData])

    const [clickExchange, setClickExchange] = React.useState<string>('')

    return (
        <PageLayout title={'Trader Map Campaign'} className="">
            <div>
                <WizButton
                    text={'New Trader Map Campaign'}
                    type="primary"
                    onClick={() => {
                        setBody(resetValue)
                        setActive('create')
                        setId('')
                    }}
                />
            </div>
            <div className="grid grid-cols-[360px_1px_1fr] gap-x-[40px] mt-[30px] w-full justify-center">
                <article className="space-y-[15px] w-full overflow-y-auto">
                    {campaigns
                        ?.sort((a, b) => new Date(b?.end_at)?.getTime() - new Date(a?.end_at)?.getTime())
                        ?.map(campaign => {
                            const isUpcoming = now < new Date(campaign?.start_at)?.getTime()
                            const isEnded =
                                now >= new Date(campaign?.end_at)?.getTime() &&
                                now >= new Date(campaign?.start_at)?.getTime()

                            return (
                                <div
                                    key={campaign?.id}
                                    className={`p-5 w-full border  bg-white rounded-[5px]  cursor-pointer relative overflow-hidden ${
                                        isEnded ? 'border-line01' : 'border-line02 hover:border-primary'
                                    }`}
                                    onClick={() => {
                                        setActive('detail')
                                        setId(campaign?.id)
                                    }}>
                                    {isEnded && (
                                        <div className="absolute top-0 left-0 bg-black/40 w-full h-full z-[1]" />
                                    )}
                                    <div className="flex gap-x-[10px] items-center">
                                        <WizImage src={campaign?.symbol_image} className="w-10 rounded-full" />
                                        <Text className="text-ti1 text-font01 w-full">{campaign?.symbol}</Text>
                                        <Text
                                            className={`py-[2px] px-1 rounded-[3px] ${
                                                isUpcoming
                                                    ? 'bg-tag_blue text-white'
                                                    : isEnded
                                                    ? 'bg-line02 text-font03'
                                                    : 'bg-font_red text-white'
                                            }  text-body3 font-bold`}>
                                            {isUpcoming ? 'Soon' : isEnded ? 'Ended' : 'Ongoing'}
                                        </Text>
                                    </div>

                                    <div className="my-[15px] h-[1px] bg-line02" />

                                    <Text className="text-h3">{campaign?.title?.en}</Text>
                                    <Text className="mt-[3px] text-body3 text-font02">({campaign?.title?.ko})</Text>

                                    <div className="my-[15px] h-[1px] bg-line02" />

                                    <div className="grid grid-cols-3 gap-x-2 justify-between gap-y-[5px]">
                                        <div>
                                            <Text className="text-body3 text-font02">Prize</Text>
                                            <Text className="text-body2 text-font01 text-left font-bold">
                                                {displayAmount(campaign?.prize_pool?.total_amount)}{' '}
                                                {campaign?.prize_pool?.currency}
                                            </Text>
                                        </div>
                                        <div>
                                            <Text className="text-body3 text-font02 text-center">Priority</Text>
                                            <Text className="text-body2 text-font01 text-center font-bold">
                                                {campaign?.priority}
                                            </Text>
                                        </div>

                                        <div>
                                            <Text className="text-body3 text-font02">Exchanges</Text>
                                            <div className="flex items-center gap-[4px] justify-start flex-wrap">
                                                {Object?.keys(campaign?.exchanges)?.map((exchange, index) => {
                                                    if (!campaign?.exchanges[exchange]) return null
                                                    return (
                                                        <div key={`${exchange}_${index}`} className="">
                                                            <WizImage
                                                                src={exchanges?.find(i => i?.name === exchange)?.icon}
                                                                className="w-3"
                                                            />
                                                        </div>
                                                    )
                                                })}
                                            </div>
                                        </div>
                                        <div className="col-span-3 flex items-center mt-2 gap-x-2">
                                            <Text className="text-body3 text-font02">Period</Text>
                                            <Text className="text-body2 text-font01 ">
                                                {`${formatUTCDate(
                                                    new Date(campaign?.start_at)?.getTime(),
                                                    9,
                                                )} ~ ${formatUTCDate(new Date(campaign?.end_at)?.getTime(), 9)}`}
                                            </Text>
                                        </div>
                                    </div>
                                </div>
                            )
                        })}
                </article>
                {active !== 'default' && (
                    <>
                        <div className="w-[1px] h-full bg-line02" />
                        <article className="relative h-full">
                            <Text className="text-h2 text-left text-font01">
                                {active === 'create' ? 'Create' : 'Edit'} Campaign
                            </Text>
                            <div
                                className={`mt-4 p-5 w-full border bg-white rounded-[5px] relative overflow-hidden h-fit border-line02`}>
                                <div className="flex items-end gap-x-[40px]">
                                    <div>
                                        <Text className="text-h3 text-font01">Symbol</Text>
                                        <div className="flex items-center gap-x-[10px] mt-[10px]">
                                            <div>
                                                <div
                                                    onClick={() => symbolImageRef?.current?.click()}
                                                    className="w-10 h-10 relative border border-line02 rounded-full cursor-pointer hover:border-primary overflow-hidden">
                                                    {body?.symbol_image ? (
                                                        <WizImage
                                                            src={`${body?.symbol_image}?type=webp&width=80`}
                                                            containerClassName="w-full"
                                                            className="w-full"
                                                        />
                                                    ) : (
                                                        <div className="absolute top-[50%] left-[50%] translate-x-[-50%] translate-y-[-50%] w-[20px]">
                                                            <WizImage src={img_placeholder} />
                                                        </div>
                                                    )}
                                                </div>
                                                <input
                                                    type={'file'}
                                                    onChange={e => onChangeThumbnail(e)}
                                                    ref={symbolImageRef}
                                                    className={'hidden'}
                                                    accept="image/jpg,image/png,image/webp"
                                                />
                                            </div>
                                            <UnderLineInput
                                                inputClassName={'w-full text-body1'}
                                                onChanged={text => setBody(prev => ({...prev, symbol: text}))}
                                                value={body.symbol}
                                                placeholder={'Symbol Name'}
                                            />
                                        </div>
                                    </div>
                                    <div className="space-y-[3px]">
                                        <Text className="text-body3 text-font02 mt-[10px]">Project ID</Text>
                                        <WizSelect
                                            customOption={list}
                                            value={
                                                list?.find(i => i.id === body.project_id)?.value || list?.at(0)?.value
                                            }
                                            onChange={id => {
                                                setBody(prev => ({
                                                    ...prev,
                                                    project_id: (list?.find(i => i.value === id)?.id as string) || '',
                                                }))
                                            }}
                                            useImage={true}
                                        />
                                        <Text className="text-body3 text-font03">({body.project_id})</Text>
                                    </div>
                                </div>

                                {/* title */}
                                <section className="mt-[30px]">
                                    <Text className="text-h3 text-font01">Title</Text>
                                    <Text className="text-body3 text-font02 mt-[10px]">English</Text>
                                    <UnderLineInput
                                        inputClassName={'w-full text-body1'}
                                        onChanged={text =>
                                            setBody(prev => ({...prev, title: {...prev.title, en: text}}))
                                        }
                                        value={body.title?.en}
                                    />
                                </section>
                                <section className="mt-[15px] ">
                                    <Text className="text-body3 text-font02">Korean</Text>
                                    <UnderLineInput
                                        inputClassName={'w-full text-body1'}
                                        onChanged={text =>
                                            setBody(prev => ({...prev, title: {...prev.title, ko: text}}))
                                        }
                                        value={body.title?.ko}
                                    />
                                </section>

                                {/* description */}
                                <section className="mt-[30px]">
                                    <Text className="text-h3 text-font01">Description</Text>
                                    <Text className="text-body3 text-font02 mt-[10px]">English</Text>
                                    <UnderLineInput
                                        inputClassName={'w-full text-body1'}
                                        onChanged={text =>
                                            setBody(prev => ({...prev, description: {...prev.description, en: text}}))
                                        }
                                        value={body.description?.en}
                                    />
                                </section>
                                <section className="mt-[15px]">
                                    <Text className="text-body3 text-font02">Korean</Text>
                                    <UnderLineInput
                                        inputClassName={'w-full text-body1'}
                                        onChanged={text =>
                                            setBody(prev => ({...prev, description: {...prev.description, ko: text}}))
                                        }
                                        value={body.description?.ko}
                                    />
                                </section>

                                {/* prize pool */}
                                <section className="mt-[30px]">
                                    <Text className="text-h3 text-font01">Prize Pool</Text>
                                    <div className="flex items-center gap-x-[20px] mt-[10px]">
                                        <div>
                                            <Text className="text-body3 text-font02">Amount</Text>
                                            <UnderLineInput
                                                inputClassName={'w-full text-body1'}
                                                onChanged={text =>
                                                    setBody(prev => ({
                                                        ...prev,
                                                        prize_pool: {...prev.prize_pool, total_amount: Number(text)},
                                                    }))
                                                }
                                                value={body.prize_pool?.total_amount}
                                            />
                                        </div>
                                        <div>
                                            <Text className="text-body3 text-font02">Currency</Text>
                                            <UnderLineInput
                                                inputClassName={'w-full text-body1'}
                                                onChanged={text =>
                                                    setBody(prev => ({
                                                        ...prev,
                                                        prize_pool: {...prev.prize_pool, currency: text},
                                                    }))
                                                }
                                                value={body.prize_pool?.currency}
                                            />
                                        </div>
                                    </div>
                                </section>

                                {/* Priority */}
                                <section className="mt-[30px] w-[100px]">
                                    <Text className="text-h3 text-font01">Priority</Text>

                                    <Text className="text-body3 text-font02 mt-[10px]">Priority</Text>
                                    <UnderLineInput
                                        inputClassName={'w-full text-body1'}
                                        onChanged={text =>
                                            setBody(prev => ({
                                                ...prev,
                                                priority: Number(text),
                                            }))
                                        }
                                        value={body.priority}
                                    />
                                </section>

                                {/* Date */}
                                <section className="mt-[30px] ">
                                    <Text className="text-h3 text-font01">Period</Text>

                                    <div className="mt-[10px] flex gap-x-[20px] items-center">
                                        <div>
                                            <Text className="text-body3 text-font02">Start Date</Text>
                                            <DateInput
                                                value={convertTimezoneToInput(body.start_at)}
                                                onChange={e => {
                                                    setBody(prev => ({
                                                        ...prev,
                                                        start_at: convertInputToTimezone(e.target.value) as string,
                                                    }))
                                                }}
                                            />
                                        </div>

                                        <div>
                                            <Text className="text-body3 text-font02">End Date</Text>
                                            <DateInput
                                                value={convertTimezoneToInput(body.end_at)}
                                                onChange={e => {
                                                    setBody(prev => ({
                                                        ...prev,
                                                        end_at: convertInputToTimezone(e.target.value) as string,
                                                    }))
                                                }}
                                            />
                                        </div>
                                    </div>
                                </section>

                                {/* Exchange */}
                                <section className="mt-[30px] ">
                                    <Text className="text-h3 text-font01">Exchange</Text>
                                    <Text className="text-body3 text-font02 mt-[10px]">Support Exchange</Text>
                                    <div className={'mt-[5px] items-center gap-[10px] flex flex-wrap'}>
                                        {exchanges?.map(exchange => (
                                            <>
                                                <div
                                                    key={exchange?.name}
                                                    onClick={e => {
                                                        setClickExchange(exchange?.name)
                                                    }}
                                                    className={`border py-[10px] px-[20px] rounded-[5px] cursor-pointer ${
                                                        body?.exchanges && body?.exchanges[exchange?.name]
                                                            ? 'bg-primary border-primary'
                                                            : 'border-line02'
                                                    }`}>
                                                    {/* <WizImage
                                                                src={exchange?.icon}
                                                                className={`w-5 cursor-pointer  ${
                                                                    body?.exchanges && body?.exchanges[exchange?.name]
                                                                        ? ''
                                                                        : 'grayscale opacity-60'
                                                                }`}
                                                                containerClassName={`p-1 border-[1.5px] rounded-[5px] ${
                                                                    clickExchange === exchange?.name
                                                                        ? 'border-primary'
                                                                        : 'border-transparent'
                                                                }`}
                                                            /> */}
                                                    <Text
                                                        className={`text-center ${
                                                            body?.exchanges && body?.exchanges[exchange?.name]
                                                                ? 'text-white'
                                                                : ''
                                                        }`}>
                                                        {exchange?.display_name}
                                                    </Text>
                                                </div>
                                            </>
                                        ))}
                                    </div>

                                    {clickExchange && (
                                        <div>
                                            <Text className="text-body3 text-font02 mt-[15px] Link">
                                                Support Exchange
                                            </Text>
                                            <UnderLineInput
                                                className="mt-[3px] w-[70%]"
                                                value={body?.exchanges?.[clickExchange] || ''}
                                                onChanged={text => {
                                                    setBody(prev => ({
                                                        ...prev,
                                                        exchanges: {
                                                            ...prev.exchanges,
                                                            [clickExchange]: text,
                                                        },
                                                    }))
                                                }}
                                            />
                                        </div>
                                    )}

                                    {/* 
                                    {body?.exchanges?.length !== 0 ? (
                                        <div className="mt-[5px] flex items-center gap-x-[5px]">
                                            {body?.exchanges?.map((e, index) => (
                                                <div
                                                    key={e}
                                                    onClick={() => {
                                                        // 클릭 시 제거
                                                        setBody(prev => ({
                                                            ...prev,
                                                            exchanges: prev.exchanges.filter(item => item !== e),
                                                        }))
                                                    }}>
                                                    <WizImage
                                                        src={exchanges?.find(i => i?.name === e)?.icon}
                                                        containerClassName="w-5 cursor-pointer"
                                                    />
                                                </div>
                                            ))}
                                        </div>
                                    ) : (
                                        <div className="h-5"></div>
                                    )} */}
                                </section>

                                {/* banner */}
                                <section className="mt-[30px]">
                                    <Text className="text-h3 text-font01">Banner Option</Text>
                                    <div className="flex items-center gap-x-[20px] mt-[10px]">
                                        <div className="w-full">
                                            <Text className="text-body3 text-font02">Banner ID (English)</Text>
                                            <UnderLineInput
                                                inputClassName={'w-full text-body1'}
                                                onChanged={text =>
                                                    setBody(prev => ({
                                                        ...prev,
                                                        banner_id: {...prev.banner_id, en: text},
                                                    }))
                                                }
                                                value={body.banner_id?.en}
                                            />
                                        </div>
                                        <div className="w-full">
                                            <Text className="text-body3 text-font02">Banner ID (Korean)</Text>
                                            <UnderLineInput
                                                inputClassName={'w-full text-body1'}
                                                onChanged={text =>
                                                    setBody(prev => ({
                                                        ...prev,
                                                        banner_id: {...prev.banner_id, ko: text},
                                                    }))
                                                }
                                                value={body.banner_id?.ko}
                                            />
                                        </div>
                                    </div>
                                    <Text className="text-body3 text-font02 mt-[20px]">Project Event Click URL</Text>
                                    <UnderLineInput
                                        inputClassName={'w-full text-body1'}
                                        onChanged={text =>
                                            setBody(prev => ({
                                                ...prev,
                                                url_third_party: text,
                                            }))
                                        }
                                        value={body.url_third_party}
                                    />
                                </section>

                                <div className="mt-6">
                                    <WizButton
                                        text={`${active === 'create' ? 'Create' : 'Edit'} Project`}
                                        type="primary"
                                        className=""
                                        size="big"
                                        onClick={async () => {
                                            const now = new Date()?.getTime()
                                            const startAt = new Date(body?.start_at)?.getTime()
                                            const endAt = new Date(body?.end_at)?.getTime()
                                            if (active === 'create') {
                                                const result = await createTraderMapCampaign({
                                                    ...body,
                                                    status: startAt > now ? 'upcoming' : endAt > now ? 'ongoing' : '',
                                                })

                                                if (result?.data && result?.status === 200) {
                                                    setActive('default')
                                                    alert(`Trader Map Campaign Created : ${body?.symbol}`)
                                                    refetch()
                                                }
                                            } else if (active === 'detail') {
                                                const result = await editTraderMapCampaign({
                                                    ...body,
                                                    id: id,
                                                    status: startAt > now ? 'upcoming' : endAt > now ? 'ongoing' : '',
                                                })
                                                console.log(result)

                                                if (result?.data && result?.status === 200) {
                                                    setActive('default')
                                                    alert(`Trader Map Campaign Edited : ${body?.symbol}`)
                                                    refetch()
                                                    detailRefetch()
                                                }
                                            }
                                        }}
                                    />
                                </div>
                            </div>
                        </article>
                    </>
                )}
            </div>
        </PageLayout>
    )
}
